import React, { useMemo, useState } from "react";
import {
  addDays,
  auditDateForRun,
  clockLabelFromMinutes,
  dayKey,
  describeSchedule,
  occurrencesByDay,
  parseDayKey,
  todayIn,
} from "./cron";

const WEEKDAY_HEADERS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const CHIPS_PER_DAY = 3;

function formatDay(key, options) {
  const { year, month, day } = parseDayKey(key);
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", ...options }).format(
    new Date(Date.UTC(year, month - 1, day))
  );
}

// Every day shown for a month: whole weeks, Sunday first.
function monthGrid(year, month) {
  const first = dayKey(year, month, 1);
  const leading = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
  const length = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const cells = Math.ceil((leading + length) / 7) * 7;
  const start = addDays(first, -leading);
  return Array.from({ length: cells }, (_, index) => addDays(start, index));
}

function shiftMonth({ year, month }, delta) {
  const date = new Date(Date.UTC(year, month - 1 + delta, 1));
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1 };
}

// Runs on one day, grouped into chips by time and by whether the schedule is enabled.
function chipsFor(runs) {
  const chips = new Map();
  for (const run of runs) {
    const enabled = run.schedule.state === "ENABLED";
    const key = `${run.minutes}|${enabled}`;
    if (!chips.has(key)) chips.set(key, { key, minutes: run.minutes, enabled, runs: [] });
    chips.get(key).runs.push(run);
  }
  return [...chips.values()].sort((a, b) => a.minutes - b.minutes || b.enabled - a.enabled);
}

function ScheduleCalendar({ schedules, describeTarget }) {
  const today = useMemo(() => todayIn(), []);
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const { year, month } = parseDayKey(today);
    return { year, month };
  });
  const [selectedDay, setSelectedDay] = useState(today);
  // Disabled schedules never run, so the calendar leaves them out unless asked.
  const [showDisabled, setShowDisabled] = useState(false);
  const shownSchedules = useMemo(
    () =>
      showDisabled
        ? schedules
        : schedules.filter((schedule) => schedule.state === "ENABLED"),
    [schedules, showDisabled]
  );
  const hiddenCount = schedules.length - shownSchedules.length;

  const days = useMemo(
    () => monthGrid(visibleMonth.year, visibleMonth.month),
    [visibleMonth]
  );
  const firstDay = days[0];
  const lastDay = days[days.length - 1];

  const { runsByDay, rateSchedules } = useMemo(() => {
    const byDay = new Map();
    const skipped = [];
    for (const schedule of shownSchedules) {
      const occurrences = occurrencesByDay(
        schedule.schedule_expression,
        schedule.schedule_expression_timezone,
        firstDay,
        lastDay,
        {
          startDate: schedule.start_date,
          endDate: schedule.end_date,
          createdAt: schedule.created_at,
        }
      );
      if (!occurrences) {
        skipped.push(schedule);
        continue;
      }
      for (const [day, runs] of occurrences) {
        if (!byDay.has(day)) byDay.set(day, []);
        for (const run of runs) byDay.get(day).push({ ...run, schedule });
      }
    }
    for (const runs of byDay.values()) {
      runs.sort(
        (a, b) => a.minutes - b.minutes || a.schedule.name.localeCompare(b.schedule.name)
      );
    }
    return { runsByDay: byDay, rateSchedules: skipped };
  }, [shownSchedules, firstDay, lastDay]);

  const selectDay = (key) => {
    setSelectedDay(key);
    const { year, month } = parseDayKey(key);
    if (year !== visibleMonth.year || month !== visibleMonth.month) {
      setVisibleMonth({ year, month });
    }
  };

  const goToToday = () => selectDay(today);
  const selectedRuns = runsByDay.get(selectedDay) || [];
  const selectedChips = chipsFor(selectedRuns);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900">
          {formatDay(dayKey(visibleMonth.year, visibleMonth.month, 1), {
            month: "long",
            year: "numeric",
          })}
        </h3>
        <div className="flex flex-wrap items-center gap-1">
          <label className="mr-3 flex items-center gap-1.5 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={showDisabled}
              onChange={(event) => setShowDisabled(event.target.checked)}
            />
            Show disabled{hiddenCount && !showDisabled ? ` (${hiddenCount})` : ""}
          </label>
          <button
            type="button"
            onClick={() => setVisibleMonth((current) => shiftMonth(current, -1))}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            aria-label="Previous month"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={goToToday}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => setVisibleMonth((current) => shiftMonth(current, 1))}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            aria-label="Next month"
          >
            ›
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="grid min-w-[720px] grid-cols-7 border-l border-t border-gray-200 text-xs">
          {WEEKDAY_HEADERS.map((label) => (
            <div
              key={label}
              className="border-b border-r border-gray-200 bg-gray-50 px-2 py-1.5 font-medium uppercase text-gray-500"
            >
              {label}
            </div>
          ))}
          {days.map((key) => {
            const { month, day } = parseDayKey(key);
            const inMonth = month === visibleMonth.month;
            const chips = chipsFor(runsByDay.get(key) || []);
            const selected = key === selectedDay;
            return (
              <button
                type="button"
                key={key}
                onClick={() => selectDay(key)}
                aria-pressed={selected}
                aria-label={`${formatDay(key, {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}, ${chips.reduce((total, chip) => total + chip.runs.length, 0)} runs`}
                className={`flex min-h-[96px] flex-col gap-1 border-b border-r border-gray-200 p-1.5 text-left align-top hover:bg-indigo-50/40 ${
                  inMonth ? "bg-white" : "bg-gray-50"
                } ${selected ? "ring-2 ring-inset ring-indigo-500" : ""}`}
              >
                <span
                  className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                    key === today
                      ? "bg-indigo-600 font-semibold text-white"
                      : inMonth
                      ? "text-gray-700"
                      : "text-gray-400"
                  }`}
                >
                  {day}
                </span>
                {chips.slice(0, CHIPS_PER_DAY).map((chip) => {
                  const runs = chip.runs.reduce((total, run) => total + run.count, 0);
                  const label =
                    chip.runs.length === 1
                      ? describeTarget(chip.runs[0].schedule).website
                      : `${chip.runs.length} schedules`;
                  return (
                    <span
                      key={chip.key}
                      title={chip.runs
                        .map((run) => describeTarget(run.schedule).website)
                        .join("\n")}
                      className={`block truncate rounded px-1.5 py-0.5 ${
                        chip.enabled
                          ? "bg-indigo-50 text-indigo-700"
                          : "bg-gray-100 text-gray-400 line-through"
                      }`}
                    >
                      <span className="font-medium">{clockLabelFromMinutes(chip.minutes)}</span>{" "}
                      {label}
                      {runs > chip.runs.length ? ` · ${runs} runs` : ""}
                    </span>
                  );
                })}
                {chips.length > CHIPS_PER_DAY && (
                  <span className="px-1.5 text-gray-500">
                    +{chips.length - CHIPS_PER_DAY} more
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {rateSchedules.length > 0 && (
        <p className="mt-2 text-xs text-gray-500">
          Not on the calendar ({rateSchedules.length}): rate() schedules with no start or
          creation date to count from —{" "}
          {rateSchedules
            .map(
              (schedule) =>
                `${schedule.name} (${describeSchedule(
                  schedule.schedule_expression,
                  schedule.schedule_expression_timezone
                )})`
            )
            .join(", ")}
          .
        </p>
      )}

      <section className="mt-5" aria-label="Runs on the selected day">
        <h3 className="font-semibold text-gray-900">
          {formatDay(selectedDay, {
            weekday: "long",
            month: "long",
            day: "numeric",
            year: "numeric",
          })}
        </h3>
        <p className="mb-3 text-xs text-gray-500">Times in ET.</p>
        {selectedChips.length ? (
          <div className="space-y-4">
            {selectedChips.map((chip) => (
              <div key={chip.key}>
                <div className="mb-1 flex items-baseline gap-2 text-sm">
                  <span className="font-medium text-gray-900">
                    {clockLabelFromMinutes(chip.minutes)}
                  </span>
                  <span className="text-xs text-gray-500">
                    {chip.runs.length} {chip.runs.length === 1 ? "schedule" : "schedules"}
                    {chip.enabled ? "" : " · disabled, will not run"}
                  </span>
                </div>
                <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                  {chip.runs.map((run) => {
                    const target = describeTarget(run.schedule);
                    const auditDate = auditDateForRun(run.schedule.input, run.utcMs);
                    // A bare date resolves to noon on the API; a fixed site_date may
                    // carry its own time (YYYY-MM-DDTHH:MM).
                    const fixedTime = /T(\d{2}):(\d{2})/.exec(run.schedule.input?.site_date || "");
                    const auditTime = fixedTime
                      ? clockLabelFromMinutes(Number(fixedTime[1]) * 60 + Number(fixedTime[2]))
                      : "noon";
                    return (
                      <li
                        key={`${run.schedule.group_name}:${run.schedule.name}:${run.minutes}`}
                        className={`flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-3 py-2 ${
                          chip.enabled ? "" : "text-gray-400"
                        }`}
                      >
                        <span className="min-w-0">
                          <span className="font-medium text-gray-900">{target.website}</span>
                          <span className="text-gray-500"> · {target.agent}</span>
                          <span className="block font-mono text-xs text-gray-400">
                            {run.schedule.name}
                          </span>
                        </span>
                        <span className="text-xs text-gray-600">
                          {run.count > 1
                            ? `${run.count} runs, from ${clockLabelFromMinutes(run.minutes)}`
                            : auditDate
                            ? `Audits the site as of ${formatDay(auditDate, {
                                weekday: "short",
                                month: "short",
                                day: "numeric",
                              })}, ${auditTime}`
                            : "Audits the live site"}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No runs scheduled on this day.</p>
        )}
      </section>
    </div>
  );
}

export default ScheduleCalendar;
